<template>
  <div class="streamgraph-wrapper" ref="wrapperRef">
    <svg :width="width" :height="height">
      <g :transform="`translate(${margin.left},${margin.top})`">
        <g v-for="boundary in periodBoundaries" :key="boundary.period">
          <line
            :x1="boundary.x" :y1="0"
            :x2="boundary.x" :y2="innerHeight"
            stroke="#ccc" stroke-width="1"
          />
          <text
            :x="boundary.x" :y="-6"
            font-size="10" fill="#888" text-anchor="middle"
          >{{ boundary.period }}</text>
        </g>

        <rect :width="innerWidth" :height="innerHeight" fill="transparent" @click="selectedName = undefined"/>

        <path
          v-for="layer in streamLayers" :key="layer.name"
          :d="layer.path"
          :fill="layer.color"
          :fill-opacity="selectedName && selectedName !== layer.name ? 0.3 : 0.8"
          style="cursor: pointer"
          @click.stop="onLayerClick(layer.name)"
        />

        <template v-for="label in nameLabels" :key="label.name">
          <text
            v-if="selectedName === label.name"
            :x="label.x" :y="label.y"
            font-size="11" fill="white" font-weight="normal"
            text-anchor="middle" dominant-baseline="middle"
            pointer-events="none"
          >{{ label.name }} ({{ label.count }})</text>
          <text
            v-else
            :x="label.x" :y="label.y"
            font-size="11" fill="white" font-weight="normal"
            text-anchor="middle" dominant-baseline="middle"
            pointer-events="none"
          >{{ label.name }}</text>
        </template>

        <template v-if="selectedName">
          <text
            v-for="pc in selectedPeriodCounts" :key="`${pc.name}-${pc.period}`"
            :x="pc.x" :y="pc.y"
            font-size="9" fill="white" fill-opacity="0.9"
            text-anchor="middle" dominant-baseline="middle"
            pointer-events="none"
          >{{ pc.count }}</text>
        </template>
      </g>
    </svg>

  </div>
</template>

<style scoped lang="scss">
.streamgraph-wrapper {
  position: relative;
  width: 100%;

  @media (max-width: 767px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}
</style>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { stack, stackOffsetWiggle, area, curveMonotoneX } from 'd3-shape'
import { scaleLinear } from 'd3-scale'
import { extent } from 'd3-array'
import type { NamePeriodCount } from './NameCounts'
import { PersonNamePart } from '@/rd/ResearchDataModel'

const props = defineProps<{
  data: NamePeriodCount[]
  namePart: PersonNamePart
}>()

const router = useRouter()

const colors = [
  '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
  '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac',
  '#6a9bc3', '#f5a960', '#e8807f', '#93ccc7', '#7ab86e',
  '#f2d974', '#c49aba', '#ffb5bf', '#b59279', '#cdc4be',
  '#3a6d94', '#d97a1e', '#c9403f', '#5a9e98', '#44843a',
]

const margin = { top: 24, right: 16, bottom: 8, left: 16 }
const chartHeight = 300
const minWidth = 768

const wrapperRef = ref<HTMLDivElement>()
const containerWidth = ref(600)
const selectedName = ref<string>()

const width = computed(() => Math.max(containerWidth.value, minWidth))
const height = chartHeight
const innerWidth = computed(() => width.value - margin.left - margin.right)
const innerHeight = computed(() => height - margin.top - margin.bottom)

const top10Names = computed(() => {
  const totals = new Map<string, number>()
  for (const d of props.data) {
    totals.set(d.name, (totals.get(d.name) ?? 0) + d.count)
  }
  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 25)
    .map(([name]) => name)
    .sort((a, b) => a.localeCompare(b))
})

const periods = computed(() => {
  const set = new Set<number>()
  for (const d of props.data) set.add(d.period)
  return [...set].sort((a, b) => a - b)
})

const stackedData = computed(() => {
  if (periods.value.length === 0 || top10Names.value.length === 0) return []

  const names = top10Names.value
  const lookup = new Map<string, Map<number, number>>()
  for (const d of props.data) {
    if (!names.includes(d.name)) continue
    let periodMap = lookup.get(d.name)
    if (!periodMap) { periodMap = new Map(); lookup.set(d.name, periodMap) }
    periodMap.set(d.period, d.count)
  }

  const rows = periods.value.map(period => {
    const row: Record<string, number> & { period: number } = { period }
    for (const name of names) {
      row[name] = lookup.get(name)?.get(period) ?? 0
    }
    return row
  })

  const stackGen = stack<typeof rows[0]>()
    .keys(names)
    .offset(stackOffsetWiggle)

  return stackGen(rows)
})

const midpoints = computed(() => periods.value.map(p => p + 25))

const xScale = computed(() => {
  if (midpoints.value.length === 0) return scaleLinear().domain([0, 1]).range([0, innerWidth.value])
  return scaleLinear()
    .domain([midpoints.value[0], midpoints.value[midpoints.value.length - 1]])
    .range([innerWidth.value, 0])
})

const yScale = computed(() => {
  if (stackedData.value.length === 0) return scaleLinear().domain([-1, 1]).range([innerHeight.value, 0])
  const all = stackedData.value.flatMap(layer => layer.flatMap(d => [d[0], d[1]]))
  const [lo, hi] = extent(all) as [number, number]
  return scaleLinear().domain([lo, hi]).range([innerHeight.value, 0])
})

const areaGen = computed(() => {
  const xS = xScale.value
  const yS = yScale.value
  return area<[number, number] & { data: { period: number } }>()
    .x(d => xS(d.data.period + 25))
    .y0(d => yS(d[0]))
    .y1(d => yS(d[1]))
    .curve(curveMonotoneX)
})

const streamLayers = computed(() => {
  return stackedData.value.map((layer, i) => ({
    name: layer.key,
    path: areaGen.value(layer as any) ?? '',
    color: colors[i % colors.length],
  }))
})

const nameLabels = computed(() => {
  const xS = xScale.value
  const yS = yScale.value
  return stackedData.value.map((layer, i) => {
    let bestIdx = 0
    let bestHeight = 0
    for (let j = 0; j < layer.length; j++) {
      const h = Math.abs(yS(layer[j][0]) - yS(layer[j][1]))
      if (h > bestHeight) { bestHeight = h; bestIdx = j }
    }
    const d = layer[bestIdx]
    const period = periods.value[bestIdx]
    const x = xS(period + 25)
    const y = (yS(d[0]) + yS(d[1])) / 2
    const count = layer[bestIdx].data[layer.key] as number
    return { name: layer.key, x, y, count, visible: bestHeight > 14, color: colors[i % colors.length] }
  }).filter(l => l.visible)
})

const selectedPeriodCounts = computed(() => {
  if (!selectedName.value) return []
  const xS = xScale.value
  const yS = yScale.value
  const layer = stackedData.value.find(l => l.key === selectedName.value)
  if (!layer) return []
  const labelIdx = nameLabels.value.find(l => l.name === selectedName.value) ? findBestIdx(layer) : -1
  const result: { name: string, period: number, count: number, x: number, y: number }[] = []
  for (let j = 0; j < layer.length; j++) {
    if (j === labelIdx) continue
    const d = layer[j]
    const count = d.data[layer.key] as number
    if (count === 0) continue
    const bandHeight = Math.abs(yS(d[0]) - yS(d[1]))
    if (bandHeight < 12) continue
    result.push({
      name: layer.key,
      period: periods.value[j],
      count,
      x: xS(periods.value[j] + 25),
      y: (yS(d[0]) + yS(d[1])) / 2,
    })
  }
  return result
})

function findBestIdx(layer: typeof stackedData.value[0]) {
  const yS = yScale.value
  let bestIdx = 0
  let bestHeight = 0
  for (let j = 0; j < layer.length; j++) {
    const h = Math.abs(yS(layer[j][0]) - yS(layer[j][1]))
    if (h > bestHeight) { bestHeight = h; bestIdx = j }
  }
  return bestIdx
}

const periodBoundaries = computed(() => {
  if (periods.value.length === 0) return []
  const xS = xScale.value
  const ps = periods.value
  const boundaries = ps.map(p => ({ period: p, x: xS(p) }))
  boundaries.push({ period: ps[ps.length - 1] + 50, x: xS(ps[ps.length - 1] + 50) })
  return boundaries
})

function onLayerClick(name: string) {
  if (selectedName.value === name) {
    router.push({ name: 'namesearch', params: { part: props.namePart, name } })
  } else {
    selectedName.value = name
  }
}

let resizeObserver: ResizeObserver | undefined

onMounted(() => {
  if (!wrapperRef.value) return
  containerWidth.value = wrapperRef.value.clientWidth
  resizeObserver = new ResizeObserver(entries => {
    containerWidth.value = entries[0].contentRect.width
  })
  resizeObserver.observe(wrapperRef.value)
})

onUnmounted(() => {
  resizeObserver?.disconnect()
})
</script>
